import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../Education.css';

const CommodityEducation: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    }
  };

  const curriculum = [
    {
      title: 'Introduction to Commodities',
      lessons: [
        'What are commodities and how are they traded',
        'Types of commodities: Metals, Energy, Agriculture',
        'Spot markets vs. Futures contracts',
        'Understanding commodity exchanges (COMEX, NYMEX, CME)',
        'Role of commodities in portfolio diversification',
        'Commodity trading hours and sessions'
      ]
    },
    {
      title: 'Precious Metals Trading',
      lessons: [
        'Gold (XAU/USD): Safe-haven and inflation hedge',
        'Silver (XAG/USD): Industrial and precious metal',
        'Platinum and Palladium trading',
        'Factors affecting precious metal prices',
        'Gold-Silver ratio and trading opportunities',
        'Central bank gold reserves and their impact'
      ]
    },
    {
      title: 'Energy Commodities',
      lessons: [
        'Crude Oil: WTI vs. Brent differences',
        'Natural Gas fundamentals and seasonality',
        'OPEC and its influence on oil prices',
        'Understanding EIA inventory reports',
        'Geopolitical events and energy markets',
        'Renewable energy impact on traditional commodities'
      ]
    },
    {
      title: 'Agricultural Commodities',
      lessons: [
        'Soft commodities: Coffee, Sugar, Cotton, Cocoa',
        'Grains: Wheat, Corn, Soybeans fundamentals',
        'Weather patterns and crop production',
        'USDA reports and their market impact',
        'Seasonal trading patterns in agriculture',
        'Supply and demand dynamics'
      ]
    },
    {
      title: 'Commodity Analysis & Strategies',
      lessons: [
        'Fundamental analysis for commodities',
        'Technical analysis specific to commodity charts',
        'Spread trading between related commodities',
        'Seasonal patterns and calendar spreads',
        'Correlation with currencies (USD, commodity currencies)',
        'Risk management in commodity trading'
      ]
    }
  ];

  const commodityCategories = [
    {
      category: 'Precious Metals',
      icon: '🥇',
      items: ['Gold', 'Silver', 'Platinum', 'Palladium'],
      use: 'Safe-haven assets, jewelry, industry'
    },
    {
      category: 'Energy',
      icon: '⚡',
      items: ['Crude Oil', 'Natural Gas', 'Heating Oil', 'Gasoline'],
      use: 'Transportation, heating, power generation'
    },
    {
      category: 'Base Metals',
      icon: '🔩',
      items: ['Copper', 'Aluminum', 'Zinc', 'Nickel'],
      use: 'Construction, manufacturing, technology'
    },
    {
      category: 'Agriculture',
      icon: '🌾',
      items: ['Wheat', 'Corn', 'Soybeans', 'Coffee', 'Sugar', 'Cotton'],
      use: 'Food production, textiles, biofuels'
    }
  ];

  return (
    <motion.div 
      className="education-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="page-content">
        <motion.div className="page-title" variants={itemVariants}>
          <h1>Commodity Trading Education</h1>
          <p>Learn to trade precious metals, energy, and agricultural commodities</p>
        </motion.div>

        <motion.section className="course-overview" variants={itemVariants}>
          <h2>Why Trade Commodities?</h2>
          <p>
            Commodities are essential raw materials that drive the global economy. Trading commodities offers unique 
            opportunities for profit, portfolio diversification, and protection against inflation. Unlike stocks or bonds, 
            commodities have intrinsic value and react differently to economic cycles.
          </p>
          <div className="course-stats">
            <div className="stat">
              <h3>Inflation Hedge</h3>
              <p>Protection</p>
            </div>
            <div className="stat">
              <h3>Low Correlation</h3>
              <p>Diversification</p>
            </div>
            <div className="stat">
              <h3>High Volatility</h3>
              <p>Trading Opportunities</p>
            </div>
            <div className="stat">
              <h3>24hr Markets</h3>
              <p>Flexible Trading</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="learning-path" variants={itemVariants}>
          <h2>Commodity Trading Curriculum</h2>
          <div className="modules-list">
            {curriculum.map((module, index) => (
              <motion.div 
                key={index} 
                className="module-card"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                <div className="module-header">
                  <span className="module-number">Module {index + 1}</span>
                </div>
                <h3>{module.title}</h3>
                <ul className="topics-list">
                  {module.lessons.map((lesson, lessonIndex) => (
                    <li key={lessonIndex}>{lesson}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="commodity-categories-section" variants={itemVariants}>
          <h2>Commodity Categories</h2>
          <div className="categories-grid">
            {commodityCategories.map((cat, index) => (
              <motion.div 
                key={index} 
                className="category-card"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
              >
                <div className="category-icon">{cat.icon}</div>
                <h3>{cat.category}</h3>
                <div className="items-list">
                  {cat.items.map((item, i) => (
                    <span key={i} className="item-badge" style={{color: '#06b6d4'}}>{item}</span>
                  ))}
                </div>
                <p className="category-use"><strong>Uses:</strong> {cat.use}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="market-factors" variants={itemVariants}>
          <h2>Key Price Drivers</h2>
          <div className="factors-grid">
            <div className="factor">
              <h4>📊 Supply & Demand</h4>
              <p>Production levels, inventory data, and consumption patterns globally</p>
            </div>
            <div className="factor">
              <h4>🌦️ Weather & Climate</h4>
              <p>Droughts, floods, and seasonal patterns affecting agricultural output</p>
            </div>
            <div className="factor">
              <h4>💱 US Dollar Strength</h4>
              <p>Inverse relationship with dollar-denominated commodities</p>
            </div>
            <div className="factor">
              <h4>🌍 Geopolitical Events</h4>
              <p>Wars, sanctions, trade policies disrupting supply chains</p>
            </div>
            <div className="factor">
              <h4>📈 Economic Growth</h4>
              <p>Industrial demand correlates with global economic expansion</p>
            </div>
            <div className="factor">
              <h4>🏭 Production Costs</h4>
              <p>Mining, extraction, and farming costs affecting profitability</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="important-reports" variants={itemVariants}>
          <h2>Critical Market Reports</h2>
          <div className="reports-grid">
            <div className="report">
              <h4 style={{color: '#06b6d4'}}>📊 EIA Reports (Energy)</h4>
              <p><strong>When:</strong> Weekly, Wednesdays 10:30 AM ET</p>
              <p>Crude oil and natural gas inventory levels</p>
            </div>
            <div className="report">
              <h4 style={{color: '#06b6d4'}}>🌾 USDA Reports (Agriculture)</h4>
              <p><strong>When:</strong> Monthly, typically around the 10th</p>
              <p>Crop production, supply, and demand estimates</p>
            </div>
            <div className="report">
              <h4 style={{color: '#06b6d4'}}>⛏️ World Metal Statistics</h4>
              <p><strong>When:</strong> Monthly</p>
              <p>Production and consumption data for metals</p>
            </div>
            <div className="report">
              <h4 style={{color: '#06b6d4'}}>🛢️ OPEC Meetings</h4>
              <p><strong>When:</strong> Bi-annually + ad-hoc</p>
              <p>Production quotas affecting global oil supply</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="trading-strategies" variants={itemVariants}>
          <h2>Commodity Trading Strategies</h2>
          <div className="strategies-grid">
            <div className="strategy">
              <h3>📈 Trend Following</h3>
              <p>Ride long-term trends driven by supply-demand imbalances</p>
            </div>
            <div className="strategy">
              <h3>📰 Event Trading</h3>
              <p>Trade around inventory reports, OPEC meetings, and weather events</p>
            </div>
            <div className="strategy">
              <h3>🔄 Spread Trading</h3>
              <p>Trade the price difference between related commodities</p>
            </div>
            <div className="strategy">
              <h3>📅 Seasonal Patterns</h3>
              <p>Exploit recurring seasonal trends in agricultural commodities</p>
            </div>
            <div className="strategy">
              <h3>🛡️ Hedging Strategies</h3>
              <p>Protect against inflation and currency devaluation</p>
            </div>
            <div className="strategy">
              <h3>💹 Correlation Trading</h3>
              <p>Trade based on relationships between commodities and currencies</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="practical-exercises" variants={itemVariants}>
          <h2>Practical Learning Activities</h2>
          <div className="exercises-list">
            <div className="exercise">
              <h4 style={{color: '#06b6d4'}}>✓ Track Oil Inventory Reports</h4>
              <p>Learn to read and trade EIA weekly petroleum status reports</p>
            </div>
            <div className="exercise">
              <h4 style={{color: '#06b6d4'}}>✓ Monitor Weather Patterns</h4>
              <p>Understand how weather affects agricultural commodity prices</p>
            </div>
            <div className="exercise">
              <h4 style={{color: '#06b6d4'}}>✓ Analyze Gold Charts</h4>
              <p>Study gold's behavior during economic uncertainty</p>
            </div>
            <div className="exercise">
              <h4 style={{color: '#06b6d4'}}>✓ Demo Trading Practice</h4>
              <p>Practice commodity trading strategies on a demo account</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="success-tips" variants={itemVariants}>
          <h2>Commodity Trading Tips</h2>
          <ul className="tips-list">
            <li>Start with gold or crude oil - most liquid commodity markets</li>
            <li>Understand the unique characteristics of each commodity</li>
            <li>Monitor weekly/monthly reports specific to your commodity</li>
            <li>Be aware of contract expiration dates when trading futures</li>
            <li>Use wider stop-losses due to commodity volatility</li>
            <li>Consider storage costs and contango/backwardation in futures</li>
            <li>Watch the US Dollar Index - inversely correlated with most commodities</li>
            <li>Stay informed about geopolitical events affecting supply</li>
          </ul>
        </motion.section>

        <motion.div className="cta-section" variants={itemVariants}>
          <h2>Master Commodity Trading</h2>
          <p>Get expert education and professional trading signals for commodities</p>
          <div className="btn-container">
            <Link to="/contact" className="btn">Enroll in Commodity Course</Link>
            <Link to="/services/signals" className="btn secondary">Get Commodity Signals</Link>
            <Link to="/markets/commodity" className="btn secondary">Explore Commodity Markets</Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default CommodityEducation;
